import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { OutlineWarning01Icon } from '@tempmee/react-icons';
import { Box, Button, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { FloatingContainer, Screen, ZStack } from '~/commonComponents';
import { BackButton } from '~/commonComponents/BackButton';
import { PaymentLockedBanner, SectionCard } from '~/components/ShiftRequestRedesign/common';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useHasValidPaymentMethod } from '~/hooks/useHasValidPaymentMethod';
import { useNavigationDetector } from '~/hooks/useNavigationDetector';
import {
  type IShiftDetailsFormValues,
  type IShiftOverride,
} from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { AutoConfirmField } from '../AddInvites/AutoConfirmField';
import { EditShiftForm } from './EditShiftForm';
import { ReadOnlyDatePill } from './ReadOnlyDatePill';

export const EditShiftScreen = () => {
  const { dateIndex } = useParams<{ dateIndex: string }>();
  const navigate = useNavigate();
  const { getValues, setValue: setSharedValue } = useFormContext<IShiftDetailsFormValues>();

  const [showNavigationGuard, setShowNavigationGuard] = useState(false);

  const onNavigationAttempt = useCallback(() => {
    setShowNavigationGuard(true);
  }, []);

  const { allowNavigation, navigateToPending, clearPendingNavigation } = useNavigationDetector({
    when: true,
    onNavigationAttempt,
  });

  const sharedValues = getValues();
  const index = Number(dateIndex);
  const date = sharedValues.dates[index];

  const dateKey = date?.toISOString() ?? '';
  const existingOverride = sharedValues.shiftOverrides?.[dateKey];

  // Compute initial defaults only once on mount to prevent form reset on re-renders
  const localDefaults = useMemo(
    () => ({
      ...sharedValues,
      ...(existingOverride ?? {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Snapshot shared values once on mount to prevent form reset
    [],
  );

  const {
    control,
    watch,
    getValues: getLocalValues,
    setValue: setLocalValue,
  } = useForm<IShiftDetailsFormValues>({
    defaultValues: localDefaults,
  });

  const professionType = watch('professionType');
  const preferredSpecialty = watch('preferredSpecialty');
  const officeId = watch('officeId');

  // Auto-confirm requires a payable office. When the office can't pay, the
  // feature is locked: the field is disabled and any stored value is cleared.
  const locked = !useHasValidPaymentMethod(officeId);

  useEffect(() => {
    if (!locked) return;

    setLocalValue(
      'autoConfirm',
      { favorites: false, eligibleProfessionals: false },
      { shouldDirty: false },
    );
  }, [locked, setLocalValue]);

  // The office name must reflect the office selected for THIS shift (form value),
  // not the globally selected office from the left sidebar.
  const childOffices = useAppSelector((state) => state.user.childOffices);
  const currentUser = useAppSelector((state) => state.user.user);
  const officeName =
    childOffices?.find((office) => office.id === officeId)?.office_name ??
    (currentUser?.id === officeId ? currentUser?.office_name : undefined) ??
    '';

  const handleBack = useCallback(() => {
    allowNavigation();
    navigate('/dashboard/multi-day/confirm');
  }, [allowNavigation, navigate]);

  const handleSave = useCallback(() => {
    const localValues = getLocalValues();
    const override: IShiftOverride = {
      preferredSpecialty: localValues.preferredSpecialty,
      isAssistedHygiene: localValues.isAssistedHygiene,
      arrivalTime: localValues.arrivalTime,
      departureTime: localValues.departureTime,
      mealBreakDuration: localValues.mealBreakDuration,
      isMealBreakPaid: localValues.isMealBreakPaid,
      rate: localValues.rate,
      procedures: localValues.procedures,
      // When the office can't pay, never persist an enabled auto-confirm —
      // guards against a stale value carried over from a prior override.
      autoConfirm: locked
        ? { favorites: false, eligibleProfessionals: false }
        : localValues.autoConfirm,
    };

    const currentOverrides = getValues('shiftOverrides') || {};
    setSharedValue('shiftOverrides', {
      ...currentOverrides,
      [dateKey]: override,
    });

    allowNavigation();
    navigate('/dashboard/multi-day/confirm');
  }, [getLocalValues, getValues, setSharedValue, dateKey, locked, allowNavigation, navigate]);

  if (!date) {
    navigate('/dashboard/multi-day/confirm', { replace: true });
    return null;
  }

  return (
    <Screen minWidth={768} paddingInline={30} paddingTop={16} paddingBottom={16}>
      <FloatingContainer
        justifyContent="flex-start"
        maxWidth={1120}
        borderRadius={0}
        margin={0}
        minWidth={0}
        width="100%"
        alignSelf="center"
      >
        <Row align="center" style={{ marginTop: 60, position: 'relative' }}>
          <ZStack left={20}>
            <BackButton onClick={handleBack} />
          </ZStack>
          <Typography as="span" bold size="heading_3" textAlign="center" style={{ flex: 1 }}>
            Edit Shift
          </Typography>
        </Row>

        <Column pt={2} style={{ paddingLeft: 28, paddingRight: 28, paddingBottom: 60 }}>
          <Row gap={6} wrap="wrap" style={{ marginTop: 32 }}>
            <Column style={{ flex: 1, minWidth: 430 }}>
              <SectionCard title="Shift Details" testId="edit-shift-details-card">
                <EditShiftForm control={control} watch={watch} officeName={officeName} />
              </SectionCard>
            </Column>

            <Column style={{ flex: 1, minWidth: 430 }}>
              <div
                data-testid="edit-shift-date-card"
                data-cy="edit-shift-date-card"
                style={{
                  backgroundColor: colors.shades[0],
                  borderRadius: 8,
                  height: 80,
                  padding: '0px 28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: `1.5px solid ${colors.neutral[100]}`,
                }}
              >
                <Row align="center" style={{ gap: 6 }}>
                  <Typography as="span" bold size="heading_3" color={colors.neutral[600]}>
                    Date
                  </Typography>
                  <Typography as="span" color={colors.neutral[500]} style={{ marginTop: 4 }}>
                    (Read-Only)
                  </Typography>
                </Row>
                <ReadOnlyDatePill date={date} />
              </div>

              <Box style={{ marginTop: 24 }}>
                <SectionCard title="Speed Up Your Coverage" testId="edit-speed-up-card">
                  {locked && (
                    <Box style={{ marginBottom: 16 }}>
                      <PaymentLockedBanner context="edit" />
                    </Box>
                  )}
                  <Box
                    aria-disabled={locked || undefined}
                    style={{
                      opacity: locked ? 0.5 : 1,
                      pointerEvents: locked ? 'none' : 'auto',
                    }}
                  >
                    <AutoConfirmField
                      control={control}
                      professionType={professionType}
                      preferredSpecialty={preferredSpecialty}
                      eventContext="edit_shift"
                      disabled={locked}
                    />
                  </Box>
                </SectionCard>
              </Box>
            </Column>
          </Row>

          <Button
            data-testid="save-changes-button"
            data-cy="save-changes-button"
            onClick={handleSave}
            size="lg"
            style={{ marginTop: 40, alignSelf: 'center' }}
          >
            Save Changes
          </Button>
        </Column>
      </FloatingContainer>

      {showNavigationGuard && (
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <OutlineWarning01Icon size={80} color={colors.warning[500]} />
            </div>
          )}
          title="Are You Sure You Want to Leave?"
          description="Your shifts haven't been posted yet. Leaving now means the progress made to your request won't be saved."
          leftButtonText="Go Back"
          leftButtonAction={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          rightButtonText="Leave Anyway"
          rightButtonAction={navigateToPending}
          closePopup={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          dataTestId="navigation-guard-popup"
        />
      )}
    </Screen>
  );
};
